import * as React from 'react'
import Image from 'next/image'
import { tw } from 'utils/classnames'

type Suffix = 'none' | 'spinner' | 'icon'

export interface AvatarTextInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  id?: string
  label?: string
  helperText?: string
  errorText?: string
  invalid?: boolean
  loading?: boolean
  avatarSrc?: string
  avatarAlt?: string
  onClear?: () => void
  suffixIcon?: React.ReactNode
  suffix?: Suffix
}

// oxlint-disable-next-line complexity
export const AvatarTextInput = ({
  ref,
  id,
  label,
  helperText,
  errorText,
  invalid,
  disabled,
  loading,
  avatarSrc,
  avatarAlt = 'Avatar',
  className,
  suffix = 'none',
  suffixIcon,
  onClear,
  value,
  defaultValue,
  ...inputProps
}: AvatarTextInputProps & {
  ref?: React.RefObject<HTMLInputElement>
}) => {
  const inputId = id
  const describedBy = React.useMemo(() => {
    const ids = []
    if (helperText) {
      ids.push(`${inputId}-help`)
    }
    if (invalid && errorText) {
      ids.push(`${inputId}-err`)
    }
    return ids.join(' ') || undefined
  }, [helperText, invalid, errorText, inputId])

  const showClear =
    typeof value === 'string' && value.length > 0 && !disabled && !loading && !!onClear

  return (
    <div className="w-full" data-testid="avatar-text-input-container">
      {label ? (
        <label htmlFor={inputId} className="mb-1 block text-[14px] leading-[20px] text-gray-700">
          {label}
        </label>
      ) : null}

      <div
        className={tw(
          'group flex w-full items-center gap-2 rounded-2 border bg-cf-white',
          'px-3 pr-2 py-2',
          'h-12',
          'border-[#1313151F]',
          !disabled && !invalid && 'hover:border-gray-300',
          !disabled &&
            !invalid &&
            'focus-within:border-gray-400 focus-within:ring-2 focus-within:ring-cf-blue-600 focus-within:ring-offset-1',
          invalid && 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500/10',
          disabled && 'bg-gray-50 text-gray-400 border-gray-200',
          className,
        )}
        aria-invalid={invalid ? true : undefined}
      >
        <div className="flex h-8 w-9 items-center pr-1">
          <div className="relative h-8 w-8 overflow-hidden rounded-full border border-[#1313151F] bg-gray-100">
            <Image
              data-testid="avatar-text-input-image"
              src={avatarSrc || ''}
              alt={avatarAlt}
              fill
              className="object-cover"
              sizes="32px"
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-1 items-center">
          <input
            id={inputId}
            data-testid="avatar-text-input"
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            className={tw(
              'peer block w-full min-w-0 bg-transparent outline-none',
              'text-[16px] leading-6 font-normal',
              'placeholder:text-gray-500 text-gray-900',
              'disabled:text-gray-400 disabled:placeholder:text-gray-400',
            )}
            aria-describedby={describedBy}
            {...inputProps}
          />

          {!showClear && (loading || suffix !== 'none') ? (
            <div className="ml-1 flex h-7 w-7 items-center justify-center">
              {loading || suffix === 'spinner' ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    opacity="0.2"
                    fill="none"
                  />
                  <path
                    d="M22 12a10 10 0 0 0-10-10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                </svg>
              ) : (
                suffixIcon
              )}
            </div>
          ) : null}
        </div>
      </div>

      {invalid && errorText ? (
        <div className="mt-1 min-h-[20px] text-[12px] leading-[16px]">
          <p id={`${inputId}-err`} className="text-red-600">
            {errorText}
          </p>
        </div>
      ) : helperText ? (
        <div className="mt-1 min-h-[20px] text-[12px] leading-[16px]">
          <p id={`${inputId}-help`} className="text-gray-500">
            {helperText}
          </p>
        </div>
      ) : null}
    </div>
  )
}

AvatarTextInput.displayName = 'AvatarTextInput'
